import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ApiError } from "go-core/api/types";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { apiPublic } from "../../../../../../services/Api/Public/apiPublic";
import {
	OrderPaymentMethodApi,
	OrderTransactionApi,
	OrderTransactionStatus,
	PayOrderData,
} from "../../../../../../services/Api/Public/types";
import HeaderBack from "../../components/HeaderBack";
import { OrderContext } from "../../contexts/OrderContext";
import { useParentGoBackMessage } from "../../hooks";
import OrderPaymentSection from "./components/OrderPaymentSection";
import OrderPaymentStickyFooter from "./components/OrderPaymentStickyFooter";

const EOrderPaymentPage = () => {
	const { orderId, order, matchUrl, setPaymentSuccess, redirectUrl } = useContext(OrderContext);
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<OrderPaymentMethodApi | undefined>();
	const form = useForm<PayOrderData>({
		criteriaMode: "all",
	});
	const { setError, handleSubmit } = form;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const [sendInvoice, setSendInvoice] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const { addFlash } = useFlash();
	const [showErrorPaymentModal, setShowErrorPaymentModal] = useState(false);
	const [customFormErrors, setCustomFormErrors] = useState<ApiError[]>([]);
	const { search } = useLocation();
	const urlSearchParams = new URLSearchParams(search);
	const history = useHistory();
	useParentGoBackMessage(false);

	const checkTransaction = async (data: OrderTransactionApi) => {
		if (!data.id) return;

		const pollTransaction = async () => {
			if (!data.id) return;

			try {
				const res = await apiPublic.getOrderTransaction(data.id);
				const status = res.status;

				if (status === OrderTransactionStatus.NEW && res.in_progress) {
					setTimeout(pollTransaction, 2000);
				} else if (status === OrderTransactionStatus.NEW || status === OrderTransactionStatus.ERROR) {
					setIsProcessing(false);
					setShowErrorPaymentModal(true);
				} else {
					setIsProcessing(false);

					if (res.redirect_url) {
						if (window.top !== window.self) {
							window.parent.postMessage({ type: "RELOAD_PARENT", url: res.redirect_url }, "*");
						} else {
							window.location.href = res.redirect_url;
						}
					} else {
						setPaymentSuccess(true);
						history.replace(matchUrl, { paymentSuccess: true });
					}
				}
			} catch (err) {
				handleError.alert(err, addFlash);
				setIsProcessing(false);
				setShowErrorPaymentModal(true);
			}
		};

		await pollTransaction();
	};

	const handlePayment = (paymentData: string, callback?: (data: OrderTransactionApi) => Promise<void> | void) =>
		handleSubmit(async (data) => {
			if (!validateCustomErrors()) return;

			if (!orderId || !order?.to_pay || !selectedPaymentMethod) return;

			setIsProcessing(true);

			const successRedirectUrlParam = `orderTransactionStatus=${OrderTransactionStatus.SUCCESS.toLowerCase()}`;
			const errorRedirectUrlParam = `orderTransactionStatus=${OrderTransactionStatus.ERROR.toLowerCase()}`;
			const finalRedirectUrl = redirectUrl !== "" ? redirectUrl : window.location.origin + matchUrl;
			const finalSuccessRedirectUrl =
				redirectUrl !== ""
					? `${finalRedirectUrl}${btoa(
							unescape(
								encodeURIComponent(`${window.location.origin + matchUrl}?${successRedirectUrlParam}`)
							)
					  )}`
					: `${finalRedirectUrl}?${successRedirectUrlParam}`;
			const finalErrorRedirectUrl =
				redirectUrl !== ""
					? `${finalRedirectUrl}${btoa(
							unescape(
								encodeURIComponent(`${window.location.origin + matchUrl}?${errorRedirectUrlParam}`)
							)
					  )}`
					: `${finalRedirectUrl}?${errorRedirectUrlParam}`;
			try {
				const tipAmount = Number(urlSearchParams.get("tipAmount"));
				const res = await apiPublic.payOrder(orderId, {
					amount: order.to_pay.amount,
					payment_data: paymentData,
					payment_method_type: selectedPaymentMethod.type,
					payment_method_id: selectedPaymentMethod.id,
					email: data.email,
					success_redirect_url: finalSuccessRedirectUrl,
					error_redirect_url: finalErrorRedirectUrl,
					tax_id_no: sendInvoice ? data.tax_id_no : undefined,
					tip_amount: tipAmount,
				});

				if (res.redirect_url) {
					if (window.top !== window.self) {
						window.parent.postMessage({ type: "RELOAD_PARENT", url: res.redirect_url }, "*");
					} else {
						window.location.href = res.redirect_url;
					}
				} else if (callback) {
					callback(res);
				}
			} catch (err) {
				handleError.form(err, form.setError, addFlash);
				setIsProcessing(false);
			}
		});

	return (
		<>
			<HeaderBack />
			<FormDirty
				formState={{
					...form.formState,
					isSubmitting: true,
				}}
			>
				<main>
					<OrderPaymentSection
						selectedPaymentMethod={selectedPaymentMethod}
						setSelectedPaymentMethod={setSelectedPaymentMethod}
						form={form}
						setErrors={setErrors}
						sendInvoice={sendInvoice}
						setSendInvoice={setSendInvoice}
						customFormErrors={customFormErrors}
					/>
				</main>
				<OrderPaymentStickyFooter
					selectedPaymentMethod={selectedPaymentMethod}
					form={form}
					handlePayment={handlePayment}
					setIsProcessing={setIsProcessing}
					isProcessing={isProcessing}
					showErrorPaymentModal={showErrorPaymentModal}
					setShowErrorPaymentModal={setShowErrorPaymentModal}
					customFormErrors={customFormErrors}
					sendInvoice={sendInvoice}
					setCustomFormErrors={setCustomFormErrors}
					checkTransaction={checkTransaction}
				/>
			</FormDirty>
		</>
	);
};

export default EOrderPaymentPage;
